import { useState, type ReactNode } from "react";
import { useSettings } from "@/shared/api/settings";
import { useAuthConfig, useMe } from "@/shared/api/hooks";
import { useI18n, LangToggle } from "@/shared/i18n";
import { StatusPill } from "./StatusPill";
import "./AppShell.css";

export type TabKey = "humanize" | "detect" | "billing" | "account";

const TABS: { key: TabKey; label: string }[] = [
  { key: "humanize", label: "Humanize" },
  { key: "detect", label: "AI Detector" },
  { key: "billing", label: "Buy Credits" },
  { key: "account", label: "Account" },
];

function ConnectionPill() {
  const { hasKey } = useSettings();
  const { t } = useI18n();
  const me = useMe();
  if (!hasKey) return <StatusPill tone="pending" dot>{t("Not connected")}</StatusPill>;
  if (me.isLoading) return <StatusPill tone="pending" dot>{t("Connecting…")}</StatusPill>;
  if (me.isError) return <StatusPill tone="error" dot>{t("Connection failed")}</StatusPill>;
  return <StatusPill tone="ok" dot>{t("Connected")}</StatusPill>;
}

function BalancePill() {
  const { hasKey } = useSettings();
  const { t } = useI18n();
  const me = useMe();
  const total =
    me.data != null ? me.data.free_tokens + me.data.paid_tokens : null;
  return (
    <span className="balance-pill">
      {t("Credits")} <b>{!hasKey ? "—" : total ?? "…"}</b>
    </span>
  );
}

function SettingsDrawer() {
  const { settings, setSettings } = useSettings();
  const { t } = useI18n();
  return (
    <div className="settings">
      <div className="settings__inner">
        <div>
          <label className="field-label" htmlFor="apiKey">
            API Key（X-API-Key）
          </label>
          <input
            id="apiKey"
            className="input mono"
            type="password"
            autoComplete="off"
            placeholder="hk_..."
            value={settings.apiKey}
            onChange={(e) => setSettings({ ...settings, apiKey: e.target.value })}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="apiBase">
            {t("API base (empty = same origin)")}
          </label>
          <input
            id="apiBase"
            className="input mono"
            type="text"
            placeholder="http://127.0.0.1:8080"
            value={settings.apiBase}
            onChange={(e) => setSettings({ ...settings, apiBase: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}

interface AppShellProps {
  tab: TabKey;
  onTab: (t: TabKey) => void;
  children: ReactNode;
}

export function AppShell({ tab, onTab, children }: AppShellProps) {
  const [showSettings, setShowSettings] = useState(false);
  const { t } = useI18n();
  // The detector tab exists only when the server has a provider configured
  // (a paid subscription); until then the product is just the humanizer.
  const detectOn = !!useAuthConfig().data?.detect_provider;
  const tabs = TABS.filter((tb) => tb.key !== "detect" || detectOn);
  return (
    <div className="shell">
      <nav className="nav">
        <a
          className="brand"
          href="/"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <span className="brand__mark" aria-hidden />
          <span className="brand__name">
            Human<b>Ink</b>
          </span>
        </a>

        <div className="tabs" role="tablist">
          {tabs.map((tb) => (
            <button
              key={tb.key}
              role="tab"
              aria-selected={tab === tb.key}
              className={`tab ${tab === tb.key ? "tab--active" : ""}`}
              onClick={() => onTab(tb.key)}
            >
              {t(tb.label)}
            </button>
          ))}
        </div>

        <div className="nav__spacer" />
        <div className="nav__right">
          <ConnectionPill />
          <BalancePill />
          <LangToggle />
          <button
            className="icon-btn"
            title={t("Connection settings")}
            aria-label={t("Connection settings")}
            aria-expanded={showSettings}
            onClick={() => setShowSettings((s) => !s)}
          >
            ⚙
          </button>
        </div>
      </nav>

      {showSettings && <SettingsDrawer />}

      <main className="page">{children}</main>

      <footer className="foot">
        HumanInk · {t("Iterative rewriting + fidelity guard · usage-based billing")}
      </footer>
    </div>
  );
}
